/**
 * Helpers shared by every "winning numbers for a given draw date" page.
 *
 * They run on the server (route loaders / head) and in the browser, so they
 * only read the deterministic static dataset — never live fetches.
 */
import { ALL_GAMES, type Game } from "@/data/games";
import { STATES } from "@/data/states";
import { getResultsForGame, getSafeTodayIso } from "@/data/results";
import type { Faq } from "@/lib/jsonld";
import { formatDate } from "@/lib/format";

const LOOKBACK = 200;

/** Resolve the game behind `/games/{slug}` or `/{state}/{slug}` URLs. */
export function findRouteGame(gameSlug: string, stateSlug?: string): Game | null {
  if (!stateSlug) {
    return ALL_GAMES.find((g) => g.slug === gameSlug && g.is_multistate) ?? null;
  }
  const state = STATES.find((s) => s.slug === stateSlug);
  if (!state) return null;
  const stored = `${stateSlug}-${gameSlug}`;
  return (
    ALL_GAMES.find(
      (g) => !g.is_multistate && g.state_id === state.id && (g.slug === stored || g.slug === gameSlug),
    ) ?? null
  );
}

export interface DrawNav {
  /** Older draw (previous in time), or null when this is the oldest. */
  prev: string | null;
  /** Newer draw, only when it is not future-dated. */
  next: string | null;
  /** Most recent draw dates for the game, newest first (excludes future dates). */
  recent: string[];
  /** Total draws we hold for the game. */
  total: number;
  /** Latest available draw date for the game. */
  latest: string | null;
}

/** Previous/next/recent draw dates around `date` for one game. */
export function drawNavigation(gameId: string, date: string, baseDate = new Date()): DrawNav {
  const today = getSafeTodayIso(baseDate);
  const dates = getResultsForGame(gameId, LOOKBACK)
    .map((r) => r.draw_date)
    .filter((d) => d <= today)
    .sort((a, b) => b.localeCompare(a));
  const i = dates.indexOf(date);
  return {
    prev: i >= 0 && i + 1 < dates.length ? dates[i + 1] : null,
    next: i > 0 ? dates[i - 1] : null,
    recent: dates.slice(0, 12),
    total: dates.length,
    latest: dates[0] ?? null,
  };
}

/** Long-tail question set crawlers and searchers expect on a draw page. */
export function resultFaqs(input: {
  gameName: string;
  dateLabel: string;
  drawSchedule?: string | null;
  stateName?: string | null;
  numbers?: number[];
  bonus?: number[];
  multiplier?: number | null;
}): Faq[] {
  const { gameName, dateLabel, drawSchedule, stateName, numbers = [], bonus = [], multiplier } = input;
  const scope = stateName ? `${stateName} ${gameName}` : gameName;
  const all = [...numbers, ...bonus];
  const faqs: Faq[] = [];
  if (all.length) {
    faqs.push({
      q: `What were the ${scope} winning numbers on ${dateLabel}?`,
      a: `The ${scope} winning numbers drawn on ${dateLabel} were ${numbers.join(", ")}${
        bonus.length ? ` with bonus ${bonus.join(", ")}` : ""
      }${multiplier ? `, and the multiplier was ${multiplier}x` : ""}.`,
    });
  }
  faqs.push({
    q: `How do I check if my ${gameName} ticket from ${dateLabel} won?`,
    a: `Compare every number on your ticket with the ${scope} numbers shown above for ${dateLabel}. Matching the bonus number alone usually wins the smallest prize, and our results checker compares a full ticket in one step.`,
  });
  faqs.push({
    q: `When is the next ${scope} drawing?`,
    a: drawSchedule
      ? `${scope} is drawn ${drawSchedule}. The newest numbers are published on this site minutes after each draw.`
      : `${scope} results are published here minutes after each drawing, and the game page always shows the latest numbers first.`,
  });
  faqs.push({
    q: `How long do I have to claim a ${scope} prize from ${dateLabel}?`,
    a: `Claim windows are set by the lottery that runs the game and typically range from 90 days to one year from the ${dateLabel} draw date. Check the official state lottery for the exact deadline before your ticket expires.`,
  });
  faqs.push({
    q: `Where can I see older ${scope} winning numbers?`,
    a: `The full ${scope} history page lists every past drawing we hold, newest first, so you can review trends or look up an older ticket.`,
  });
  return faqs;
}

/**
 * Social-card metadata for a single draw. Repeats the drawn numbers and draw
 * date in og:/twitter: tags so link previews show the result itself rather
 * than a generic page title.
 */
export function drawSocialMeta(input: {
  scope: string;            // e.g. "Texas Lotto"
  date: string;             // ISO draw date
  dateLabel: string;        // human draw date
  numbers: number[];
  bonus?: number[];
  multiplier?: number | null;
}): Array<Record<string, string>> {
  const { scope, date, dateLabel, numbers, bonus = [], multiplier } = input;
  const numbersText = numbers.join(", ");
  const bonusText = bonus.length ? ` + ${bonus.join(", ")}` : "";
  const social = numbersText
    ? `${scope} — ${dateLabel}: ${numbersText}${bonusText}`
    : `${scope} winning numbers — ${dateLabel}`;
  const detail = numbersText
    ? `Winning numbers drawn ${dateLabel}: ${numbersText}${bonusText}${multiplier ? ` (x${multiplier})` : ""}.`
    : `Winning numbers for the ${scope} drawing on ${dateLabel}.`;
  return [
    { property: "og:title", content: social },
    { property: "og:description", content: detail },
    { property: "article:published_time", content: `${date}T00:00:00Z` },
    { property: "article:modified_time", content: `${date}T00:00:00Z` },
    { property: "article:section", content: "Lottery results" },
    { name: "twitter:title", content: social },
    { name: "twitter:description", content: detail },
    { name: "twitter:label1", content: "Draw date" },
    { name: "twitter:data1", content: dateLabel },
    { name: "twitter:label2", content: "Winning numbers" },
    { name: "twitter:data2", content: numbersText || "See page" },
  ];
}

/** Latest available draw for a game, used for history-page social previews. */
export function latestDrawSummary(gameSlug: string, stateSlug?: string): {
  dateLabel: string;
  date: string;
  numbersText: string;
} | null {
  const game = findRouteGame(gameSlug, stateSlug);
  if (!game) return null;
  const [latest] = getResultsForGame(game.id, 1);
  if (!latest) return null;
  return {
    date: latest.draw_date,
    dateLabel: formatDate(latest.draw_date),
    numbersText: (latest.winning_numbers ?? []).join(", "),
  };
}
